import { useCallback, useMemo, useRef } from 'react';
import cn from 'classnames';
import { type CellStatusColor, IconEditFilled, Text, TextCell, VStack } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { type OpenApiSchemaPriceIndexSignal, type OpenApiSchemaProductVariantListItem } from '#shared/api/schema';
import {
    type ChangedVariantPriceOnSave,
    ChangeVariantPriceTooltip,
} from '#shared/components/ChangeVariantPriceTooltip';
import { type GridRow, type GridRowExtended } from '#shared/components/grid';
import { MAP_INDEX_PRICE_ICON, MAP_INDEX_PRICE_ICON_COLOR, MAP_INDEX_PRICE_TITLE } from '#shared/lib/product';
import { format } from '#shared/lib/string';
import { StatefulTooltip } from '#shared/ui/StatefulTooltip';
import { updateProductVariant } from '../../api/api';
import { createProductBasePriceRenderer } from '../ProductBasePrice';
import styles from './ProductVariantPriceCell.module.css';

type ProductVariantPriceCellProps = {
    'data-testid'?: string;
    className?: string;
    onClick?: () => void;
    withPriceIndex?: boolean;
    data: GridRow<OpenApiSchemaProductVariantListItem> | GridRowExtended<OpenApiSchemaProductVariantListItem>;
};

export function ProductVariantPriceCell({
    'data-testid': dataTestId,
    data,
    className,
    withPriceIndex = false,
    onClick,
}: ProductVariantPriceCellProps) {
    const { price, minimal_price, final_price, price_index_summary } = data.attributes;

    const signal: OpenApiSchemaPriceIndexSignal = price_index_summary?.signal ?? 'no_data';
    const PriceIndexIcon = MAP_INDEX_PRICE_ICON[signal];

    const isError = !final_price;

    const isMinimalPriceAbovePrice = Number(minimal_price) > Number(price);

    const statusColor = useMemo<CellStatusColor | undefined>(() => {
        if (!price) {
            return 'error';
        }

        if (isMinimalPriceAbovePrice) {
            return 'warning';
        }

        return undefined;
    }, [price, isMinimalPriceAbovePrice]);

    const { value: isHoverEditIcon, toggleOn: startHoverEditIcon, toggleOff: stopHoverEditIcon } = useToggle();

    const {
        value: isChangeVariantPriceTooltipOpen,
        toggleOn: openChangeVariantPriceTooltip,
        toggleOff: closeChangeVariantPriceTooltip,
    } = useToggle(false);
    const editTooltipTriggerRef = useRef<HTMLDivElement>(null);
    const handleSavePriceVariant = useCallback<ChangedVariantPriceOnSave>(
        (variant) =>
            updateProductVariant({
                params: {
                    id: variant.id,
                },
                payload: variant,
            }),
        []
    );

    const canEditPrice = useMemo(() => data.attributes.status !== 'ARCHIVED', [data.attributes.status]);

    return (
        <>
            <TextCell
                ref={editTooltipTriggerRef}
                onClick={canEditPrice ? openChangeVariantPriceTooltip : onClick}
                className={className}
                data-testid={dataTestId}
                onHoverStart={startHoverEditIcon}
                onHoverEnd={stopHoverEditIcon}
                title={(textProps) => {
                    if (isError) {
                        return (
                            <Text {...textProps} color="red">
                                Укажите цену
                            </Text>
                        );
                    }

                    return <Text {...textProps}>{format(final_price ?? '', 'price')}</Text>;
                }}
                statusColor={statusColor}
                align="right"
                caption={createProductBasePriceRenderer(data.attributes)}
                iconRight={
                    <VStack gap="4" className={styles.iconRightStack}>
                        {!isError && withPriceIndex ? (
                            <StatefulTooltip title={MAP_INDEX_PRICE_TITLE[signal]} placement="top">
                                <PriceIndexIcon
                                    size="16"
                                    color={MAP_INDEX_PRICE_ICON_COLOR[signal]}
                                    role={signal === 'no_data' || signal === 'bad_data' ? 'supplementary' : 'main'}
                                />
                            </StatefulTooltip>
                        ) : undefined}
                        {canEditPrice ? (
                            <IconEditFilled
                                size="16"
                                className={cn(styles.editIcon, {
                                    [styles.editIconHover]: isHoverEditIcon || isChangeVariantPriceTooltipOpen,
                                })}
                                color="gray"
                                role="supplementary"
                            />
                        ) : undefined}
                    </VStack>
                }
            />
            <ChangeVariantPriceTooltip
                isOpen={isChangeVariantPriceTooltipOpen}
                onClose={closeChangeVariantPriceTooltip}
                triggerRef={editTooltipTriggerRef}
                variant={data.attributes}
                onSave={handleSavePriceVariant}
            />
        </>
    );
}
